<template>
  <div class="px-2">
    <vue-element-loading
      :active="demoLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#1e293b"
      spinner="bar-fade-scale"
    />

    <v-snackbar v-model="showsnackbar" color="#0f172a" right :timeout="timeout">
      <span style="color: #FFFFFF; font-weight: 600;">{{ msg }}</span>
      <template v-slot:action="{ attrs }">
        <v-btn text color="#FFFFFF" v-bind="attrs" @click="showsnackbar = false">
          <v-icon style="color: #FFFFFF">mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <!-- EXECUTIVE HEADER -->
    <header class="sa-hero-header mb-4">
      <div class="sa-hero-text">
        <PageBreadcrumb :items="[{ text: 'Demo Requests' }]" class="mb-1" />
        <div class="d-flex align-center flex-wrap gap-2">
          <span class="role-badge-pill">Leads &amp; Onboarding</span>
        </div>
        <p class="sa-subtitle">
          Manage prospective client demo bookings, schedule walk-through calls, and track store conversions
        </p>
      </div>

      <div class="sa-hero-actions">
        <button class="action-btn outline-btn mr-2" @click="fetchDemoBookings()" title="Refresh list">
          <v-icon left size="16" color="#334155">mdi-refresh</v-icon>
          Refresh
        </button>
        <button class="action-btn outline-btn" @click="$router.push('/superadminDashboard')">
          <v-icon left size="16" color="#334155">mdi-account-plus-outline</v-icon>
          Add Jewellery Admin
        </button>
      </div>
    </header>

    <!-- PRO KPI RIBBON CARDS -->
    <v-row class="mb-4">
      <v-col cols="12" sm="6" md="3" class="py-1">
        <v-card outlined class="pa-4 rounded-lg flex-column justify-center" color="#FFFFFF">
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="kpi-title">TOTAL REQUESTS</span>
            <v-icon small color="#334155">mdi-calendar-clock-outline</v-icon>
          </div>
          <div class="kpi-val">{{ totalCount }} <small class="text-caption">Bookings</small></div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3" class="py-1">
        <v-card outlined class="pa-4 rounded-lg flex-column justify-center" color="#FFFFFF">
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="kpi-title">PENDING APPROVAL</span>
            <v-icon small color="#D97706">mdi-clock-alert-outline</v-icon>
          </div>
          <div class="kpi-val text-amber">{{ pendingCount }}</div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3" class="py-1">
        <v-card outlined class="pa-4 rounded-lg flex-column justify-center" color="#FFFFFF">
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="kpi-title">SCHEDULED CALLS</span>
            <v-icon small color="#2563EB">mdi-calendar-check-outline</v-icon>
          </div>
          <div class="kpi-val text-blue">{{ scheduledCount }}</div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3" class="py-1">
        <v-card outlined class="pa-4 rounded-lg flex-column justify-center" color="#FFFFFF">
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="kpi-title">COMPLETED DEMOS</span>
            <v-icon small color="#047857">mdi-check-decagram-outline</v-icon>
          </div>
          <div class="kpi-val text-green">{{ completedCount }}</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- FILTER & SEARCH BAR -->
    <div class="filter-search-wrap mb-4">
      <div class="type-filter-group">
        <button
          class="type-pill"
          :class="{ active: selectedStatusFilter === 'ALL' }"
          @click="selectedStatusFilter = 'ALL'"
        >
          All ({{ totalCount }})
        </button>
        <button
          class="type-pill"
          :class="{ active: selectedStatusFilter === 'Pending' }"
          @click="selectedStatusFilter = 'Pending'"
        >
          Pending ({{ pendingCount }})
        </button>
        <button
          class="type-pill"
          :class="{ active: selectedStatusFilter === 'Scheduled' }"
          @click="selectedStatusFilter = 'Scheduled'"
        >
          Scheduled ({{ scheduledCount }})
        </button>
        <button
          class="type-pill"
          :class="{ active: selectedStatusFilter === 'Completed' }"
          @click="selectedStatusFilter = 'Completed'"
        >
          Completed ({{ completedCount }})
        </button>
        <button
          class="type-pill"
          :class="{ active: selectedStatusFilter === 'Cancelled' }"
          @click="selectedStatusFilter = 'Cancelled'"
        >
          Cancelled
        </button>
      </div>

      <div class="search-input-wrap">
        <v-text-field
          v-model="searchQuery"
          prepend-inner-icon="mdi-magnify"
          placeholder="Search client, shop name, phone..."
          outlined
          dense
          hide-details
          clearable
          class="search-field"
        ></v-text-field>
      </div>
    </div>

    <!-- NO DATA STATE -->
    <div v-if="!demoLoading && filteredBookings.length === 0" class="no-data-box text-center py-8 my-4">
      <v-icon color="#64748b" large class="mb-2">mdi-calendar-remove-outline</v-icon>
      <div class="no-data-text">No demo booking requests found matching your filter criteria.</div>
    </div>

    <!-- MAIN DEMO REQUESTS TABLE CARD -->
    <v-card v-if="filteredBookings.length > 0" outlined class="rounded-lg overflow-hidden elevation-1 mb-5" color="#FFFFFF">
      <div class="table-container">
        <table class="pro-demo-table">
          <thead>
            <tr>
              <th class="text-left sticky-col">CLIENT / STORE NAME</th>
              <th class="text-left">CONTACT DETAILS</th>
              <th class="text-center">PREFERRED SCHEDULE</th>
              <th class="text-left">NOTES / REMARKS</th>
              <th class="text-center">STATUS</th>
              <th class="text-center" style="min-width: 170px;">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in filteredBookings" :key="item._id || index" class="table-row">
              <!-- CLIENT / STORE NAME -->
              <td class="text-left sticky-col font-weight-bold">
                <div class="d-flex align-center">
                  <v-avatar color="#F1F5F9" size="32" class="mr-3">
                    <span class="avatar-text">{{ (item.name || 'C').charAt(0).toUpperCase() }}</span>
                  </v-avatar>
                  <div>
                    <div class="client-name">{{ item.name || '-' }}</div>
                    <div class="shop-name-text">
                      <v-icon size="12" color="#64748b" class="mr-1">mdi-store</v-icon>
                      {{ item.jewelleryName || 'Single Store' }}
                    </div>
                  </div>
                </div>
              </td>

              <!-- CONTACT DETAILS -->
              <td class="text-left">
                <div class="contact-line">
                  <v-icon size="13" color="#64748b" class="mr-1">mdi-email-outline</v-icon>
                  <span>{{ item.email || '-' }}</span>
                </div>
                <div class="contact-line text-muted mt-1">
                  <v-icon size="13" color="#64748b" class="mr-1">mdi-phone-outline</v-icon>
                  <span>{{ item.phone || '-' }}</span>
                </div>
              </td>

              <!-- PREFERRED SCHEDULE -->
              <td class="text-center">
                <div class="schedule-date">{{ formatDate(item.preferredDate) || '-' }}</div>
                <div class="slot-pill mt-1" v-if="item.preferredTime">
                  <v-icon size="11" class="mr-1">mdi-clock-outline</v-icon>
                  {{ item.preferredTime }}
                </div>
              </td>

              <!-- NOTES -->
              <td class="text-left max-notes-col">
                <span class="notes-text" :title="item.notes">
                  {{ item.notes || '—' }}
                </span>
              </td>

              <!-- STATUS BADGE -->
              <td class="text-center">
                <span class="pro-status-badge" :class="getStatusBadgeClass(item.status)">
                  {{ item.status || 'Pending' }}
                </span>
              </td>

              <!-- ACTIONS -->
              <td class="text-center">
                <div class="d-flex align-center justify-center actions-row">
                  <!-- View Details -->
                  <v-btn
                    x-small
                    outlined
                    color="#334155"
                    class="action-icon-btn"
                    @click="openViewDialog(item)"
                    title="View Full Details"
                  >
                    <v-icon size="14">mdi-eye-outline</v-icon>
                  </v-btn>

                  <!-- Change Status Dropdown -->
                  <v-menu offset-y left>
                    <template v-slot:activator="{ on, attrs }">
                      <button class="status-change-btn" v-bind="attrs" v-on="on">
                        <span>Status</span>
                        <v-icon size="12">mdi-chevron-down</v-icon>
                      </button>
                    </template>
                    <v-list dense class="status-dropdown-list pa-1">
                      <v-list-item @click="updateDemoStatus(item._id, 'Pending')">
                        <v-icon small color="#D97706" class="mr-2">mdi-clock-outline</v-icon>
                        <v-list-item-title>Set Pending</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="updateDemoStatus(item._id, 'Scheduled')">
                        <v-icon small color="#2563EB" class="mr-2">mdi-calendar-clock</v-icon>
                        <v-list-item-title>Schedule Demo</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="updateDemoStatus(item._id, 'Completed')">
                        <v-icon small color="#047857" class="mr-2">mdi-check-circle-outline</v-icon>
                        <v-list-item-title>Mark Completed</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="updateDemoStatus(item._id, 'Cancelled')">
                        <v-icon small color="#4B5563" class="mr-2">mdi-close-circle-outline</v-icon>
                        <v-list-item-title>Cancel Request</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>

                  <!-- Delete -->
                  <v-btn
                    x-small
                    icon
                    color="#EF4444"
                    @click="deleteDemoBooking(item._id)"
                    title="Delete Request"
                  >
                    <v-icon size="15">mdi-delete-outline</v-icon>
                  </v-btn>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </v-card>

    <!-- VIEW BOOKING DETAILS MODAL -->
    <v-dialog v-model="viewDialog" max-width="560px" class="pro-dialog">
      <v-card v-if="selectedBooking" class="rounded-xl overflow-hidden pa-0" color="#FFFFFF">
        <!-- Dialog Header -->
        <div class="dialog-header px-5 py-4 d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-avatar color="#F1F5F9" size="36" class="mr-3">
              <span class="avatar-text-lg">{{ (selectedBooking.name || 'C').charAt(0).toUpperCase() }}</span>
            </v-avatar>
            <div>
              <h3 class="dialog-title">{{ selectedBooking.name }}</h3>
              <div class="dialog-subtitle">{{ selectedBooking.jewelleryName || 'Jewellery Store' }}</div>
            </div>
          </div>
          <button class="close-btn" @click="viewDialog = false">
            <v-icon size="18" color="#64748b">mdi-close</v-icon>
          </button>
        </div>

        <v-divider></v-divider>

        <!-- Dialog Body -->
        <div class="pa-5">
          <div class="status-summary-bar mb-4 p-3 rounded-lg d-flex align-center justify-space-between">
            <span class="text-caption font-weight-bold text-uppercase text-muted">Current Booking Status</span>
            <span class="pro-status-badge" :class="getStatusBadgeClass(selectedBooking.status)">
              {{ selectedBooking.status || 'Pending' }}
            </span>
          </div>

          <v-row class="details-grid">
            <v-col cols="12" sm="6" class="py-2">
              <div class="detail-label">Client Name</div>
              <div class="detail-val">{{ selectedBooking.name || '-' }}</div>
            </v-col>

            <v-col cols="12" sm="6" class="py-2">
              <div class="detail-label">Store / Shop Name</div>
              <div class="detail-val">{{ selectedBooking.jewelleryName || '-' }}</div>
            </v-col>

            <v-col cols="12" sm="6" class="py-2">
              <div class="detail-label">Email Address</div>
              <div class="detail-val">
                <a :href="'mailto:' + selectedBooking.email" class="link-text">{{ selectedBooking.email || '-' }}</a>
              </div>
            </v-col>

            <v-col cols="12" sm="6" class="py-2">
              <div class="detail-label">Phone Number</div>
              <div class="detail-val">
                <a :href="'tel:' + selectedBooking.phone" class="link-text">{{ selectedBooking.phone || '-' }}</a>
              </div>
            </v-col>

            <v-col cols="12" sm="6" class="py-2">
              <div class="detail-label">Preferred Date</div>
              <div class="detail-val">{{ formatDate(selectedBooking.preferredDate) || '-' }}</div>
            </v-col>

            <v-col cols="12" sm="6" class="py-2">
              <div class="detail-label">Preferred Slot</div>
              <div class="detail-val">{{ selectedBooking.preferredTime || '-' }}</div>
            </v-col>

            <v-col cols="12" class="py-2">
              <div class="detail-label">Booking Submission Date</div>
              <div class="detail-val">{{ formatDate(selectedBooking.create_date) || '-' }}</div>
            </v-col>

            <v-col cols="12" class="py-2">
              <div class="detail-label mb-1">Client Notes / Special Instructions</div>
              <div class="notes-box">
                {{ selectedBooking.notes || 'No additional notes or special requirements provided.' }}
              </div>
            </v-col>
          </v-row>
        </div>

        <v-divider></v-divider>

        <!-- Dialog Footer Actions -->
        <div class="px-5 py-3 dialog-footer d-flex align-center justify-space-between">
          <div class="d-flex align-center gap-1">
            <span class="text-caption font-weight-medium mr-2">Quick Status:</span>
            <button class="quick-status-btn text-blue" @click="updateDemoStatusInModal('Scheduled')">
              Schedule
            </button>
            <button class="quick-status-btn text-green" @click="updateDemoStatusInModal('Completed')">
              Complete
            </button>
            <button class="quick-status-btn text-gray" @click="updateDemoStatusInModal('Cancelled')">
              Cancel
            </button>
          </div>

          <v-btn text height="36" class="text-none font-weight-bold" color="#64748b" @click="viewDialog = false">
            Close
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "DemoRequestsPage",
  data() {
    return {
      demoLoading: false,
      showsnackbar: false,
      timeout: 2500,
      msg: null,
      demoBookings: [],
      viewDialog: false,
      selectedBooking: null,
      selectedStatusFilter: "ALL",
      searchQuery: "",
    };
  },
  computed: {
    totalCount() {
      return this.demoBookings.length;
    },
    pendingCount() {
      return this.demoBookings.filter(b => (b.status || "").toLowerCase() === "pending").length;
    },
    scheduledCount() {
      return this.demoBookings.filter(b => (b.status || "").toLowerCase() === "scheduled").length;
    },
    completedCount() {
      return this.demoBookings.filter(b => (b.status || "").toLowerCase() === "completed").length;
    },
    filteredBookings() {
      let list = this.demoBookings;
      if (this.selectedStatusFilter !== "ALL") {
        const filterStr = this.selectedStatusFilter.toLowerCase();
        list = list.filter(b => (b.status || "pending").toLowerCase() === filterStr);
      }
      if (this.searchQuery && this.searchQuery.trim()) {
        const q = this.searchQuery.trim().toLowerCase();
        list = list.filter(b =>
          (b.name || "").toLowerCase().includes(q) ||
          (b.jewelleryName || "").toLowerCase().includes(q) ||
          (b.email || "").toLowerCase().includes(q) ||
          (b.phone || "").toLowerCase().includes(q) ||
          (b.notes || "").toLowerCase().includes(q)
        );
      }
      return list;
    }
  },
  mounted() {
    this.fetchDemoBookings();
  },
  methods: {
    openViewDialog(item) {
      this.selectedBooking = item;
      this.viewDialog = true;
    },
    fetchDemoBookings() {
      this.demoLoading = true;
      axios({
        url: "/user/demobookings",
        method: "GET",
        headers: {
          "x-auth-token": localStorage.getItem("token")
        }
      })
        .then(response => {
          this.demoLoading = false;
          if (response.data && response.data.status === true) {
            this.demoBookings = response.data.data || [];
          } else {
            this.msg = (response.data && response.data.msg) || "Failed to fetch bookings.";
            this.showsnackbar = true;
          }
        })
        .catch(err => {
          this.demoLoading = false;
          console.error("Error loading demo bookings:", err);
        });
    },
    updateDemoStatus(id, status) {
      axios({
        url: `/user/demobookings/${id}`,
        method: "PUT",
        data: { status },
        headers: {
          "x-auth-token": localStorage.getItem("token")
        }
      })
        .then(response => {
          if (response.data && response.data.status === true) {
            this.msg = response.data.msg || `Status updated to ${status}`;
            this.showsnackbar = true;
            this.fetchDemoBookings();
            if (this.selectedBooking && this.selectedBooking._id === id) {
              this.selectedBooking.status = status;
            }
          } else {
            this.msg = (response.data && response.data.msg) || "Failed to update status.";
            this.showsnackbar = true;
          }
        })
        .catch(err => {
          console.error("Error updating demo status:", err);
        });
    },
    updateDemoStatusInModal(status) {
      if (this.selectedBooking && this.selectedBooking._id) {
        this.updateDemoStatus(this.selectedBooking._id, status);
      }
    },
    deleteDemoBooking(id) {
      if (confirm("Are you sure you want to delete this demo booking request?")) {
        axios({
          url: `/user/demobookings/${id}`,
          method: "DELETE",
          headers: {
            "x-auth-token": localStorage.getItem("token")
          }
        })
          .then(response => {
            if (response.data && response.data.status === true) {
              this.msg = response.data.msg || "Booking deleted";
              this.showsnackbar = true;
              this.fetchDemoBookings();
              if (this.selectedBooking && this.selectedBooking._id === id) {
                this.viewDialog = false;
              }
            } else {
              this.msg = (response.data && response.data.msg) || "Failed to delete booking.";
              this.showsnackbar = true;
            }
          })
          .catch(err => {
            console.error("Error deleting demo:", err);
          });
      }
    },
    getStatusBadgeClass(status) {
      const s = (status || "").toLowerCase();
      if (s === "scheduled") return "badge-scheduled";
      if (s === "completed") return "badge-completed";
      if (s === "cancelled") return "badge-cancelled";
      return "badge-pending";
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

/* Executive Header */
.sa-hero-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  font-family: 'Plus Jakarta Sans', sans-serif;
}

.sa-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  color: #64748b;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.sa-main-title {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.3px;
  margin: 0;
  line-height: 1.2;
}

.role-badge-pill {
  display: inline-flex;
  align-items: center;
  background: #f8fafc;
  border: 1px solid #cbd5e1;
  color: #334155;
  font-size: 11.5px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 6px;
  margin-left: 8px;
}

.sa-subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 0 0;
}

.sa-hero-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.action-btn {
  height: 38px;
  padding: 0 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
}

.outline-btn {
  background: #ffffff;
  border: 1px solid #cbd5e1;
  color: #334155;
}

.outline-btn:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

/* KPI Cards */
.kpi-title {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.8px;
  color: #64748b;
  text-transform: uppercase;
}

.kpi-val {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
}

.text-amber { color: #D97706; }
.text-blue { color: #2563EB; }
.text-green { color: #047857; }

/* Filter & Search */
.filter-search-wrap {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  background: #FFFFFF;
  padding: 14px 18px;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
}

.type-filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.type-pill {
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid #E2E8F0;
  background: #F8FAFC;
  color: #475569;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.type-pill:hover {
  background: #F1F5F9;
  color: #0F172A;
}

.type-pill.active {
  background: #0F172A;
  color: #FFFFFF;
  border-color: #0F172A;
}

.search-input-wrap {
  width: 260px;
}

.no-data-box {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
}

.no-data-text {
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
}

/* Table Styling */
.table-container {
  overflow-x: auto;
  width: 100%;
}

.pro-demo-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13.5px;
  font-family: 'Plus Jakarta Sans', sans-serif;
}

.pro-demo-table th {
  background: #F8FAFC;
  color: #475569;
  font-size: 11.5px;
  font-weight: 700;
  padding: 10px 16px;
  vertical-align: middle;
  border-bottom: 1px solid #E2E8F0;
  white-space: nowrap;
  letter-spacing: 0.3px;
}

.pro-demo-table td {
  padding: 16px 18px;
  vertical-align: middle;
  border-bottom: 1px solid #F1F5F9;
  white-space: nowrap;
  color: #0F172A;
}

.table-row:hover td {
  background-color: #F8FAFC !important;
}

.table-row:hover .sticky-col {
  background-color: #F8FAFC !important;
}

.sticky-col {
  position: sticky;
  left: 0;
  z-index: 2;
  background: #FFFFFF !important;
  border-right: 2px solid #E2E8F0 !important;
  min-width: 200px;
}

th.sticky-col {
  background: #F8FAFC !important;
  color: #475569 !important;
}

.avatar-text {
  font-size: 13px;
  font-weight: 700;
  color: #334155;
}

.avatar-text-lg {
  font-size: 16px;
  font-weight: 700;
  color: #334155;
}

.client-name {
  font-size: 13.5px;
  font-weight: 700;
  color: #0F172A;
}

.shop-name-text {
  font-size: 12px;
  color: #64748B;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.contact-line {
  font-size: 12.5px;
  color: #334155;
  display: flex;
  align-items: center;
}

.schedule-date {
  font-size: 13px;
  font-weight: 600;
  color: #0F172A;
}

.slot-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 12px;
  background: #F1F5F9;
  color: #475569;
  font-size: 11px;
  font-weight: 600;
}

.max-notes-col {
  max-width: 220px;
  white-space: normal !important;
}

.notes-text {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: 12.5px;
  color: #475569;
  line-height: 1.4;
}

/* Status Badges */
.pro-status-badge {
  display: inline-block;
  padding: 3px 12px;
  border-radius: 16px;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.2px;
}

.badge-pending {
  background: #FFF7ED;
  color: #C2410C;
  border: 1px solid #FDBA74;
}

.badge-scheduled {
  background: #EFF6FF;
  color: #1D4ED8;
  border: 1px solid #93C5FD;
}

.badge-completed {
  background: #ECFDF5;
  color: #047857;
  border: 1px solid #6EE7B7;
}

.badge-cancelled {
  background: #F3F4F6;
  color: #4B5563;
  border: 1px solid #E5E7EB;
}

/* Actions */
.actions-row {
  gap: 8px;
}

.action-icon-btn {
  border-radius: 6px !important;
  height: 28px !important;
  width: 28px !important;
  min-width: 28px !important;
  padding: 0 !important;
}

.status-change-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 6px;
  border: 1px solid #CBD5E1;
  background: #FFFFFF;
  color: #334155;
  font-size: 11.5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
}

.status-change-btn:hover {
  background: #F8FAFC;
  border-color: #94a3b8;
}

/* Dialog */
.dialog-header {
  background: #F8FAFC;
}

.dialog-title {
  font-size: 17px;
  font-weight: 700;
  color: #0F172A;
  margin: 0;
}

.dialog-subtitle {
  font-size: 12.5px;
  color: #64748B;
}

.close-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
}

.close-btn:hover {
  background: #E2E8F0;
}

.status-summary-bar {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
}

.detail-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #64748B;
  text-transform: uppercase;
}

.detail-val {
  font-size: 13.5px;
  font-weight: 600;
  color: #0F172A;
  margin-top: 2px;
}

.link-text {
  color: #2563EB;
  text-decoration: none;
}

.link-text:hover {
  text-decoration: underline;
}

.notes-box {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  color: #334155;
  line-height: 1.5;
}

.dialog-footer {
  background: #F8FAFC;
}

.quick-status-btn {
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11.5px;
  font-weight: 700;
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  cursor: pointer;
  transition: all 0.15s ease;
}

.quick-status-btn:hover {
  background: #F1F5F9;
}
</style>
